import OpencareAnalytics from '@opencare/analytics';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { PrimaryButton } from 'brand-guidelines/Buttons-styled-components';
import { useFormValidator } from 'common/Validator/Validator';
import { StepFormWrapper, StepHeader, StepHeaderSubtext, StepHeaderText, StepWrapper } from 'pages/checkout/components/common/layout-styled-components';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState } from 'pages/checkout/connect/types';
import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { useCheckoutRedesignAnalyticsData } from 'pages/checkout/utils/analyticsUtils';
import { BooleanChoice, booleanChoiceToBoolean, booleanToBooleanChoice } from 'types/BooleanChoice';
import { useEffectOnce } from 'utils/customHooks/useEffectOnce';
import { usePatch } from 'utils/customHooks/usePatch';

import { FormInputConfig, FormInputs } from '../Insurance/renderer-helpers';

import { SURVEY_VALIDATOR_CONFIG } from './form-validator-config';
import { FORM_INPUT_CONFIGS } from './survey-questions';

const analytics = new OpencareAnalytics();

export const Survey = () => {
  const analyticsData = useCheckoutRedesignAnalyticsData();
  useEffectOnce(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-survey',
      name: 'checkout',
      section: 'survey',
      ...analyticsData,
    });
  });

  const { survey: surveyDataRedux } = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);

  const [urgent, setUrgent] = useState<BooleanChoice | null>(
    surveyDataRedux?.urgent === null
      ? null
      : booleanToBooleanChoice(surveyDataRedux?.urgent),
  );
  const [treatments, setTreatments] = useState<string[]>(surveyDataRedux?.treatments || []);
  const [treatmentsOther, setTreatmentsOther] = useState<string>(surveyDataRedux?.treatmentsOther || '');
  const [urgentDetails, setUrgentDetails] = useState<string>(surveyDataRedux?.urgentDetails || '');
  const [urgentDetailsOther, setUrgentDetailsOther] = useState<string>(surveyDataRedux?.urgentDetailsOther || '');
  const [anxiety, setAnxiety] = useState<string>(surveyDataRedux?.anxiety || '');
  const [lastVisited, setLastVisited] = useState<string>(surveyDataRedux?.lastVisited || '');

  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const formState = useMemo(
    () => ({
      treatments,
      treatmentsOther,
      urgent,
      urgentDetails,
      urgentDetailsOther,
      anxiety,
      lastVisited,
    }),
    [lastVisited, anxiety, treatments, treatmentsOther, urgent, urgentDetails, urgentDetailsOther],
  );

  const surveyFormValidator = useFormValidator(SURVEY_VALIDATOR_CONFIG);

  const formValidity = useMemo(() => {
    const urgentBoolean = booleanChoiceToBoolean(urgent as BooleanChoice);

    const validationResult = surveyFormValidator.validate(
      formState,
      { urgent: urgentBoolean },
    );

    return validationResult;
  }, [formState, urgent, surveyFormValidator]);

  const surveyInputs = useMemo<FormInputConfig[]>(() => [
    FORM_INPUT_CONFIGS.getUrgent({
      value: urgent,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setUrgent(e.target.value as BooleanChoice),
      errorMessage: showValidationErrors && formValidity.urgent?.message,
    }),
    FORM_INPUT_CONFIGS.getTreatments({
      condition: urgent === BooleanChoice.No,
      value: treatments,
      onSelect: setTreatments,
      errorMessage: showValidationErrors && formValidity.treatments?.message,
    }),
    FORM_INPUT_CONFIGS.getTreatmentsOther({
      condition: urgent === BooleanChoice.No && treatments.includes('other'),
      value: treatmentsOther,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTreatmentsOther(e.target.value),
      errorMessage: showValidationErrors && formValidity.treatmentsOther?.message,
    }),
    FORM_INPUT_CONFIGS.getUrgentDetails({
      condition: urgent === BooleanChoice.Yes,
      value: urgentDetails,
      onSelect: setUrgentDetails,
      errorMessage: showValidationErrors && formValidity.urgentDetails?.message,
    }),
    FORM_INPUT_CONFIGS.getUrgentDetailsOther({
      condition: urgent === BooleanChoice.Yes && Boolean(urgentDetails),
      value: urgentDetailsOther,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setUrgentDetailsOther(e.target.value),
      errorMessage: showValidationErrors && formValidity.urgentDetailsOther?.message,
    }),

    FORM_INPUT_CONFIGS.getLastVisited({
      value: lastVisited,
      onSelect: setLastVisited,
      errorMessage: showValidationErrors && formValidity.lastVisited?.message,
    }),
    FORM_INPUT_CONFIGS.getAnxiety({
      value: anxiety,
      onSelect: setAnxiety,
      errorMessage: showValidationErrors && formValidity.anxiety?.message,
    }),
  ], [
    anxiety,
    lastVisited,
    treatments,
    treatmentsOther,
    urgent,
    urgentDetails,
    urgentDetailsOther,
    formValidity,
    showValidationErrors,
  ]);

  const handleDisabledButtonClick = useCallback(() => {
    if (formValidity.allValid) {
      return;
    }

    setShowValidationErrors(true);
  }, [formValidity]);

  const patchCheckout = usePatch<CheckoutState>({ stateHandler: CheckoutStateHandler });
  const { goToNextStep } = useNextCheckoutStep();

  const handleNextStep = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formValidity.allValid) {
      setShowValidationErrors(true);
      return;
    }

    patchCheckout({
      survey: {
        ...formState,
        urgent: booleanChoiceToBoolean(formState.urgent as BooleanChoice),
      },
    });

    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-survey',
      name: 'checkout',
      section: 'survey',
      content: { ...analyticsData.content },
    });

    goToNextStep();
  };

  return (
    <StepWrapper as="form" onSubmit={handleNextStep}>
      <StepHeader>
        <StepHeaderText>Personal Information</StepHeaderText>
        <StepHeaderSubtext>
          We'd like to learn a bit more about your dental needs so we can
          match you with the right care and prepare for your upcoming visit.
        </StepHeaderSubtext>
      </StepHeader>

      <StepFormWrapper>
        <FormInputs inputConfigs={surveyInputs} />
      </StepFormWrapper>

      <PrimaryButton
        type="submit"
        onClick={handleDisabledButtonClick}
        $disabled={!formValidity.allValid}
      >
        Continue
      </PrimaryButton>
    </StepWrapper>
  );
};
